import { Component, EventEmitter, forwardRef, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { Subscription, Subject, Observable, startWith, map } from 'rxjs';
import { ControllerItem } from 'src/app/display-product-data/controllers/controller.model';
import { DisplayProductDataService } from 'src/app/display-product-data/display-product-data.service';
import { nameSort } from '../../util';

@Component({
    selector: 'app-coefficient-controller-select',
    templateUrl: './coefficient-controller-select.component.html',
    styleUrls: ['./coefficient-controller-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CoefficientControllerSelectComponent),
            multi: true
        }
    ],
    standalone: false
})
export class CoefficientControllerSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {

  private subscriptions: Subscription[] = [];
  dropdownList: string[] = [];
  controllerList: ControllerItem[] = [];
  controllerControl = new UntypedFormControl('', [
    Validators.required,
    this.optionNotFound.bind(this),
  ]);
  filteredOptions: Set<string>;
  public readonly controllerChanges: Subject<string> = new Subject<string>();
  public controllerSuggestions: Observable<string[]>;

  @Output() autocompleteChange = new EventEmitter<MatAutocompleteSelectedEvent>();
  @Output() inputChange = new EventEmitter<string>();

  constructor(
    private router: Router,
    private displayProductData: DisplayProductDataService,
  ) { }

  ngOnInit() {

    const getControllerList = this.displayProductData.getControllerList().subscribe(data => {
      this.controllerList = nameSort(data);
      this.dropdownList = this.controllerList.map(item => item.name);
      this.dropdownList.unshift('Add new controller');
      this.controllerSuggestions = this.controllerChanges.pipe(
        startWith(''),
        map((val: string) => this.filterResults(val))
      );
    })
    this.subscriptions.push(getControllerList);

  }

  private filterResults(val: string) {
    if (val !== '') {
      return this.dropdownList.filter(item => item.toLowerCase().includes(val.toLowerCase()));
    } else {
      return this.dropdownList;
    }
  }

  onFocus() {
    this.controllerChanges.next('');
  }

  updateController(value: string) {
    if (value === '') {
      this.optionNotFound(this.controllerControl);
    } else {
      
      const item = this.dropdownList.find(item => item === value);
      if (item) {
        this.inputChange.emit(value);
      } else {
        this.optionNotFound(this.controllerControl);
      }
    };
  }

  setControllerChangeAction(event: MatAutocompleteSelectedEvent) {
    if ( event.option.value === 'Add new controller' ) {
      this.router.navigate(['/data/controller/new']);
    } else {
      this.autocompleteChange.emit(event);
    }
  }

  optionNotFound(control: AbstractControl): { [s: string]: boolean } {
    const value = control.value;
    this.filteredOptions = new Set(
      this.dropdownList.filter((option) => option.toLowerCase() === value.toLowerCase())
    );
    if (value && !this.filteredOptions.size) {
      return {
        noOption: true
      };
    }
    return null;
  }

  navigateTo(value: string) {
    const item = this.controllerList.find(item => item.name === value);
    this.router.navigate([`/data/controller/${item.uuid}`]);
  }

  // model --> view
  writeValue(value): void {
    if (value) {
      this.controllerControl.setValue(value, { emitEvent: false });
    } else {
      this.controllerControl.reset('');
    }
  }
  
  // view --> model
  registerOnChange(fn: (value: string) => void) {
    const onChangeSubscription = this.controllerControl.valueChanges.subscribe(fn);
    this.subscriptions.push(onChangeSubscription);
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  onTouched: () => void = () => {};

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

}
